import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export default function NotificationScheduler() {
  const { user } = useAuth();

  // Helper to post a message to the service worker with fallback to active worker
  const postMessageToServiceWorker = async (message: Record<string, any>) => {
    try {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message);
        return true;
      }

      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage(message);
        return true;
      }
    } catch (error) {
      console.warn("NotificationScheduler: SW message failed:", error);
    }
    return false;
  };

  useEffect(() => {
    const scheduleNotificationsOnLogin = async () => {
      if (!user?.id || !("Notification" in window)) {
        return;
      }

      try {
        // Load user's notification preferences
        const { data, error } = await supabase
          .from("user_preferences")
          .select("daily_notification_enabled, daily_notification_time")
          .eq("user_id", user.id)
          .single();

        if (error && error.code !== "PGRST116") { // PGRST116 is "not found"
          console.error("Error loading notification preferences for scheduling:", error);
          return;
        }

        if (data?.daily_notification_enabled && data.daily_notification_time) {
          const time = data.daily_notification_time.substring(0, 5);
          console.log(`Scheduling notification for ${time}`);

          // Store in localStorage for persistence
          localStorage.setItem('habitNotificationTime', time);
          localStorage.setItem('habitNotificationEnabled', 'true');

          // Try to also schedule in service worker if available (with fallback)
          const swScheduled = await postMessageToServiceWorker({
            type: "SCHEDULE_DAILY_NOTIFICATION",
            time: time,
          });
          if (swScheduled) {
            console.log("SW notification scheduling attempted");
          } else {
            console.log("SW not available for scheduling, relying on browser fallback");
          }

          // Schedule browser-based notification
          scheduleBrowserNotification(time);
        } else {
          // Clear stored preferences if notifications are disabled
          localStorage.removeItem('habitNotificationTime');
          localStorage.removeItem('habitNotificationEnabled');
        }
      } catch (error) {
        console.error("Error scheduling notifications on login:", error);
      }
    };

    const scheduleBrowserNotification = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      const now = new Date();
      const targetTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);

      if (targetTime <= now) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      const timeUntilNotification = targetTime.getTime() - now.getTime();

      const timeoutId = setTimeout(() => {
        if (Notification.permission === "granted") {
          new Notification("Daily Habit Reminder 🗓️", {
            body: "Time to log your habits and maintain your streaks!",
            icon: "/logo.png",
            silent: false,
          });
          console.log("Browser notification fired at", new Date().toLocaleTimeString());

          // Notify SW (if available) that notification was shown
          postMessageToServiceWorker({
            type: "NOTIFICATION_SHOWN",
            time: time,
          });
        }
        // Schedule next one
        scheduleBrowserNotification(time);
      }, timeUntilNotification);

      (window as any).__habitNotificationTimeout = timeoutId;
      console.log(`Next notification scheduled for ${targetTime.toLocaleString()}`);
    };

    // Check for notifications when page becomes visible (main notification system)
    const checkNotificationsOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        const storedTime = localStorage.getItem('habitNotificationTime');
        const enabled = localStorage.getItem('habitNotificationEnabled') === 'true';

        if (enabled && storedTime) {
          checkAndShowNotification(storedTime);
        }
      }
    };

    const checkAndShowNotification = (time: string) => {
      const [targetHour, targetMinute] = time.split(':').map(Number);
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Check if it's time (within a 2-minute window for reliability)
      const hourMatches = currentHour === targetHour;
      const minuteDiff = Math.abs(currentMinute - targetMinute);
      const timeMatches = hourMatches && minuteDiff <= 2;

      // Also check if we've recently missed the time (within 5 minutes past)
      const currentTotalMinutes = currentHour * 60 + currentMinute;
      const targetTotalMinutes = targetHour * 60 + targetMinute;
      const minutesSinceTarget = currentTotalMinutes - targetTotalMinutes;
      const missedRecently = minutesSinceTarget >= 0 && minutesSinceTarget <= 5;

      if (timeMatches || missedRecently) {
        const lastCheck = localStorage.getItem('lastNotificationCheck');
        const lastCheckTime = lastCheck ? new Date(lastCheck).getTime() : 0;
        const hoursSinceLastCheck = (now.getTime() - lastCheckTime) / (1000 * 60 * 60);

        // Only show if we haven't shown one in the last 20 hours (to avoid duplicates)
        if (hoursSinceLastCheck >= 20) {
          console.log(`Showing notification - time matches! Current: ${currentHour}:${currentMinute}, Target: ${time}`);
          if (Notification.permission === "granted") {
            new Notification("Daily Habit Reminder 🗓️", {
              body: "Time to log your habits and maintain your streaks!",
              icon: "/logo.png",
              silent: false,
            });
            localStorage.setItem('lastNotificationCheck', now.toISOString());

            // Notify service worker that notification was shown (for coordination)
            if (navigator.serviceWorker.controller) {
              navigator.serviceWorker.controller.postMessage({
                type: "NOTIFICATION_SHOWN",
                time: time
              });
            }
          }
        } else {
          console.log(`Skipping notification - too soon since last check (${hoursSinceLastCheck.toFixed(1)} hours ago)`);
        }
      }
    };

    // Schedule notifications when user logs in
    if (user) {
      scheduleNotificationsOnLogin();
    }

    // Check for notifications when page becomes visible (main mechanism)
    document.addEventListener('visibilitychange', checkNotificationsOnVisibility);

    // Also check every 30 seconds regardless of visibility (for background support)
    const intervalId = setInterval(() => {
      const storedTime = localStorage.getItem('habitNotificationTime');
      if (storedTime && localStorage.getItem('habitNotificationEnabled') === 'true') {
        checkAndShowNotification(storedTime);
      }
    }, 30000); // Check every 30 seconds for better background support

    // Cleanup
    return () => {
      if ((window as any).__habitNotificationTimeout) {
        clearTimeout((window as any).__habitNotificationTimeout);
      }
      document.removeEventListener('visibilitychange', checkNotificationsOnVisibility);
      clearInterval(intervalId);
    };
  }, [user]);

  // This component doesn't render anything
  return null;
}
